import { parse, stringify } from 'query-string';
import { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { RoutePaths } from 'constant';

interface Props {
  localStorageKey: string;
}

const ALLOWED_SEARCH_PARAMS = ['criteria', 'type', 'viewId'];
const ALLOWED_HASH_PARAMS = ['rowGroup', 'columnGroup'];

const filterParams = (queryString: string, allowedValues: string[]) => {
  const parsedParams = parse(queryString) || {};

  const filteredParams = Object.keys(parsedParams)
    .filter((key) => allowedValues.includes(key))
    .reduce((obj, key) => {
      return {
        ...obj,
        [key]: parsedParams[key],
      };
    }, {});

  return stringify(filteredParams, { sort: false });
};

export const computeUrlToCache = (search: string, hash: string): string => {
  const parsedSearch = filterParams(search, ALLOWED_SEARCH_PARAMS);
  const parsedHash = filterParams(hash, ALLOWED_HASH_PARAMS);

  return `${parsedSearch}#${parsedHash}`;
};

export const useFiltersCaching = ({ localStorageKey }: Props): void => {
  const { pathname, search, hash } = useLocation();
  const url = computeUrlToCache(search, hash);
  const history = useHistory();
  const lastVisitedUrl = useRef(url);
  const [replaceByPreviousVisit, setReplaceByPreviousVisit] = useState(false);

  useEffect(() => {
    if (pathname === RoutePaths.tasks) {
      lastVisitedUrl.current = url;
    }
  }, [pathname, url]);

  useEffect(() => {
    if (replaceByPreviousVisit) {
      return;
    }
    if (search) {
      setReplaceByPreviousVisit(true);

      return;
    }
    const timeout = setTimeout(() => {
      const currentLocalStorageValue =
        localStorage.getItem(localStorageKey) ?? '';
      const [storedSearch, storedHash] = currentLocalStorageValue.split('#');

      setReplaceByPreviousVisit(true);
      if (currentLocalStorageValue !== url) {
        history.replace({
          pathname,
          search: storedSearch,
          hash: storedHash,
        });
      }
    }, 10);

    // eslint-disable-next-line consistent-return
    return () => clearTimeout(timeout);
  }, [
    hash,
    history,
    localStorageKey,
    pathname,
    replaceByPreviousVisit,
    search,
    url,
  ]);

  useEffect(() => {
    const onUnload = () => {
      if (
        lastVisitedUrl.current === '' ||
        lastVisitedUrl.current == null ||
        lastVisitedUrl.current === '#'
      ) {
        return;
      }
      localStorage.setItem(localStorageKey, lastVisitedUrl.current);
    };

    window.addEventListener('unload', onUnload);

    return () => {
      onUnload();
      window.removeEventListener('unload', onUnload);
    };
  }, [localStorageKey]);
};
